import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radio,
  Plus,
  Send,
  Users,
  BarChart3,
  MousePointerClick,
  Eye,
  Mail,
  AlertTriangle,
  Search,
  Filter,
  MoreHorizontal,
  Copy,
  Trash2,
  Pause,
  Play,
  ChevronLeft,
  Clock,
  CheckCircle,
  XCircle,
  UserMinus,
  Inbox,
  Megaphone,
  Zap,
  ArrowRight,
  Calendar,
  Hash,
  Timer,
  FileText,
  Tag,
  RefreshCw,
  X,
} from "lucide-react";
import { campaignsApi, audiencesApi, emailConnectionsApi, clientsApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import FormField from "../ui/FormField";

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

type TabKey = "campaigns" | "audiences" | "unsubscribes";

// ─── Types ─────────────────────────────────────────────────────────────────

interface Campaign {
  _id: string;
  name: string;
  subject: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "paused" | "failed";
  audienceId?: string;
  audienceName?: string;
  recipientCount: number;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  bouncedCount: number;
  failedCount: number;
  emailConnectionId?: string;
  emailConnectionEmail?: string;
  body?: string;
  scheduledAt?: string;
  batchSize?: number;
  batchDelaySecs?: number;
  createdAt: string;
  updatedAt: string;
}

interface Audience {
  _id: string;
  name: string;
  description?: string;
  type: "static" | "dynamic";
  estimatedCount: number;
  filters?: {
    practiceAreas?: string[];
    tags?: string[];
    matterStatus?: string;
    dateRange?: { from?: string; to?: string };
  };
  clientIds?: string[];
  createdAt: string;
}

interface Recipient {
  _id: string;
  name: string;
  email: string;
  status: "pending" | "sent" | "delivered" | "opened" | "clicked" | "bounced" | "failed";
  sentAt?: string;
  openedAt?: string;
  clickedAt?: string;
}

interface EmailConnection {
  _id: string;
  email: string;
  provider: string;
  status: string;
}

// ─── Status Badge ──────────────────────────────────────────────────────────

const statusConfig: Record<string, { bg: string; text: string; dot?: string }> = {
  draft: { bg: "bg-slate-100", text: "text-slate-700" },
  scheduled: { bg: "bg-blue-50", text: "text-blue-700" },
  sending: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  sent: { bg: "bg-emerald-50", text: "text-emerald-700" },
  paused: { bg: "bg-orange-50", text: "text-orange-700" },
  failed: { bg: "bg-red-50", text: "text-red-700" },
  pending: { bg: "bg-slate-100", text: "text-slate-600" },
  delivered: { bg: "bg-blue-50", text: "text-blue-700" },
  opened: { bg: "bg-emerald-50", text: "text-emerald-700" },
  clicked: { bg: "bg-purple-50", text: "text-purple-700" },
  bounced: { bg: "bg-orange-50", text: "text-orange-700" },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.dot && (
        <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot} ${status === "sending" ? "animate-pulse" : ""}`} />
      )}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ─── Merge Variable Chip ───────────────────────────────────────────────────

const MergeChip: React.FC<{ variable: string; onClick: () => void }> = ({ variable, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 text-xs cursor-pointer hover:bg-emerald-100 transition-colors font-medium"
  >
    {variable}
  </button>
);

// ─── Main Page ─────────────────────────────────────────────────────────────

const BroadcastsPage: React.FC = () => {
  const { userData } = useUserDataContext();
  const [activeTab, setActiveTab] = useState<TabKey>("campaigns");

  // Campaigns state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignRecipients, setCampaignRecipients] = useState<Recipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientTotal, setRecipientTotal] = useState(0);

  // Audiences state
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loadingAudiences, setLoadingAudiences] = useState(true);
  const [showCreateAudience, setShowCreateAudience] = useState(false);
  const [editingAudience, setEditingAudience] = useState<Audience | null>(null);

  // Email connections
  const [emailConnections, setEmailConnections] = useState<EmailConnection[]>([]);
  const [hasEmailConnections, setHasEmailConnections] = useState<boolean | null>(null);

  // Clients for audience builder
  const [clients, setClients] = useState<SelectOption[]>([]);

  // Campaign form state
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    subject: "",
    body: "",
    emailConnectionId: "",
    audienceId: "",
    manualClientIds: [] as string[],
    useManualSelection: false,
    scheduleType: "now" as "now" | "scheduled",
    scheduledAt: "",
    batchSize: 50,
    batchDelaySecs: 2,
  });
  const [campaignStep, setCampaignStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);

  // Audience form state
  const [audienceForm, setAudienceForm] = useState({
    name: "",
    description: "",
    type: "static" as "static" | "dynamic",
    clientIds: [] as string[],
    filters: {
      practiceAreas: [] as string[],
      tags: [] as string[],
      matterStatus: "",
      dateRange: { from: "", to: "" },
    },
  });
  const [savingAudience, setSavingAudience] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalRecipients: 0,
    avgOpenRate: 0,
    avgClickRate: 0,
  });

  // ─── Data Loading ────────────────────────────────────────────────────────

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const params: Record<string, any> = {};
      if (campaignFilter !== "all") params.status = campaignFilter;
      if (campaignSearch) params.search = campaignSearch;
      const res = await campaignsApi.list(params);
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : [];
      setCampaigns(list);

      // Compute stats
      const totalRecipients = list.reduce((s: number, c: Campaign) => s + (c.recipientCount || 0), 0);
      const sentCampaigns = list.filter((c: Campaign) => c.status === "sent" && c.sentCount > 0);
      const avgOpen = sentCampaigns.length
        ? sentCampaigns.reduce((s: number, c: Campaign) => s + (c.openedCount / c.sentCount) * 100, 0) / sentCampaigns.length
        : 0;
      const avgClick = sentCampaigns.length
        ? sentCampaigns.reduce((s: number, c: Campaign) => s + (c.clickedCount / c.sentCount) * 100, 0) / sentCampaigns.length
        : 0;
      setStats({
        totalCampaigns: list.length,
        totalRecipients,
        avgOpenRate: Math.round(avgOpen),
        avgClickRate: Math.round(avgClick),
      });
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [campaignFilter, campaignSearch]);

  const fetchAudiences = useCallback(async () => {
    setLoadingAudiences(true);
    try {
      const res = await audiencesApi.list();
      const data = res.data?.data || res.data || [];
      setAudiences(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch audiences:", err);
    } finally {
      setLoadingAudiences(false);
    }
  }, []);

  const fetchEmailConnections = useCallback(async () => {
    try {
      const res = await emailConnectionsApi.list();
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : [];
      setEmailConnections(list);
      setHasEmailConnections(list.length > 0);
    } catch (err) {
      console.error("Failed to fetch email connections:", err);
      setHasEmailConnections(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await clientsApi.list({ limit: 500 });
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : [];
      setClients(
        list.map((c: any) => ({
          value: c._id,
          label: c.displayName || c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.email || "Unknown",
        }))
      );
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  }, []);

  const fetchCampaignRecipients = useCallback(async (campaignId: string, page = 1) => {
    setLoadingRecipients(true);
    try {
      const res = await campaignsApi.recipients(campaignId, { page, limit: 20 });
      const data = res.data?.data || res.data || [];
      setCampaignRecipients(Array.isArray(data) ? data : []);
      setRecipientTotal(res.data?.pagination?.total || 0);
      setRecipientPage(page);
    } catch (err) {
      console.error("Failed to fetch recipients:", err);
    } finally {
      setLoadingRecipients(false);
    }
  }, []);

  useEffect(() => {
    fetchEmailConnections();
    fetchClients();
  }, [fetchEmailConnections, fetchClients]);

  useEffect(() => {
    if (activeTab === "campaigns") fetchCampaigns();
    if (activeTab === "audiences") fetchAudiences();
  }, [activeTab, fetchCampaigns, fetchAudiences]);

  // ─── Campaign CRUD ───────────────────────────────────────────────────────

  const handleSaveCampaign = async () => {
    setSavingCampaign(true);
    try {
      const payload: Record<string, any> = {
        name: campaignForm.name,
        subject: campaignForm.subject,
        body: campaignForm.body,
        emailConnectionId: campaignForm.emailConnectionId,
        batchSize: campaignForm.batchSize,
        batchDelaySecs: campaignForm.batchDelaySecs,
      };
      if (campaignForm.useManualSelection) {
        payload.manualClientIds = campaignForm.manualClientIds;
      } else {
        payload.audienceId = campaignForm.audienceId;
      }
      if (campaignForm.scheduleType === "scheduled" && campaignForm.scheduledAt) {
        payload.scheduledAt = campaignForm.scheduledAt;
      }

      await campaignsApi.create(payload);
      setShowCreateCampaign(false);
      resetCampaignForm();
      fetchCampaigns();
    } catch (err) {
      console.error("Failed to create campaign:", err);
    } finally {
      setSavingCampaign(false);
    }
  };

  const handleSendCampaign = async (id: string) => {
    try {
      await campaignsApi.send(id);
      fetchCampaigns();
      if (selectedCampaign?._id === id) {
        const res = await campaignsApi.get(id);
        setSelectedCampaign(res.data?.data || res.data);
      }
    } catch (err) {
      console.error("Failed to send campaign:", err);
    }
  };

  const handlePauseCampaign = async (id: string) => {
    try {
      await campaignsApi.pause(id);
      fetchCampaigns();
    } catch (err) {
      console.error("Failed to pause campaign:", err);
    }
  };

  const handleDuplicateCampaign = async (id: string) => {
    try {
      await campaignsApi.duplicate(id);
      fetchCampaigns();
    } catch (err) {
      console.error("Failed to duplicate campaign:", err);
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;
    try {
      await campaignsApi.delete(id);
      if (selectedCampaign?._id === id) setSelectedCampaign(null);
      fetchCampaigns();
    } catch (err) {
      console.error("Failed to delete campaign:", err);
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: "",
      subject: "",
      body: "",
      emailConnectionId: "",
      audienceId: "",
      manualClientIds: [],
      useManualSelection: false,
      scheduleType: "now",
      scheduledAt: "",
      batchSize: 50,
      batchDelaySecs: 2,
    });
    setCampaignStep(0);
  };

  // ─── Audience CRUD ───────────────────────────────────────────────────────

  const handleSaveAudience = async () => {
    setSavingAudience(true);
    try {
      const payload: Record<string, any> = {
        name: audienceForm.name,
        description: audienceForm.description,
        type: audienceForm.type,
      };
      if (audienceForm.type === "static") {
        payload.clientIds = audienceForm.clientIds;
      } else {
        payload.filters = audienceForm.filters;
      }

      if (editingAudience) {
        await audiencesApi.update(editingAudience._id, payload);
      } else {
        await audiencesApi.create(payload);
      }
      setShowCreateAudience(false);
      setEditingAudience(null);
      resetAudienceForm();
      fetchAudiences();
    } catch (err) {
      console.error("Failed to save audience:", err);
    } finally {
      setSavingAudience(false);
    }
  };

  const handleDeleteAudience = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this audience?")) return;
    try {
      await audiencesApi.delete(id);
      fetchAudiences();
    } catch (err) {
      console.error("Failed to delete audience:", err);
    }
  };

  const resetAudienceForm = () => {
    setAudienceForm({
      name: "",
      description: "",
      type: "static",
      clientIds: [],
      filters: { practiceAreas: [], tags: [], matterStatus: "", dateRange: { from: "", to: "" } },
    });
  };

  const openEditAudience = (audience: Audience) => {
    setEditingAudience(audience);
    setAudienceForm({
      name: audience.name,
      description: audience.description || "",
      type: audience.type,
      clientIds: audience.clientIds || [],
      filters: {
        practiceAreas: audience.filters?.practiceAreas || [],
        tags: audience.filters?.tags || [],
        matterStatus: audience.filters?.matterStatus || "",
        dateRange: { from: audience.filters?.dateRange?.from || "", to: audience.filters?.dateRange?.to || "" },
      },
    });
    setShowCreateAudience(true);
  };

  // ─── Merge variable insertion ────────────────────────────────────────────

  const insertMergeVariable = (variable: string) => {
    setCampaignForm((prev) => ({
      ...prev,
      body: prev.body + variable,
    }));
  };

  // ─── Tabs ────────────────────────────────────────────────────────────────

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: "campaigns", label: "Campaigns", icon: <Megaphone className="h-4 w-4" /> },
    { key: "audiences", label: "Audiences", icon: <Users className="h-4 w-4" /> },
    { key: "unsubscribes", label: "Unsubscribes", icon: <UserMinus className="h-4 w-4" /> },
  ];

  // ─── Summary Cards ──────────────────────────────────────────────────────

  const summaryCards = [
    { label: "Total Campaigns", value: String(stats.totalCampaigns), icon: <Megaphone className="h-5 w-5" />, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Total Recipients", value: String(stats.totalRecipients), icon: <Users className="h-5 w-5" />, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Avg Open Rate", value: `${stats.avgOpenRate}%`, icon: <Eye className="h-5 w-5" />, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Avg Click Rate", value: `${stats.avgClickRate}%`, icon: <MousePointerClick className="h-5 w-5" />, color: "text-purple-600", bg: "bg-purple-50" },
  ];

  // Connection options for SearchableSelect
  const connectionOptions: SelectOption[] = emailConnections.map((c) => ({
    value: c._id,
    label: `${c.email} (${c.provider})`,
  }));

  const audienceOptions: SelectOption[] = audiences.map((a) => ({
    value: a._id,
    label: `${a.name} (${a.estimatedCount} recipients)`,
  }));

  // ─── Campaign Detail View ───────────────────────────────────────────────

  const openCampaignDetail = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    fetchCampaignRecipients(campaign._id);
  };

  if (selectedCampaign) {
    const c = selectedCampaign;
    const detailStats = [
      { label: "Sent", value: c.sentCount || 0, icon: <Send className="h-4 w-4" />, color: "text-blue-600", bg: "bg-blue-50" },
      { label: "Delivered", value: (c.sentCount || 0) - (c.bouncedCount || 0) - (c.failedCount || 0), icon: <CheckCircle className="h-4 w-4" />, color: "text-emerald-600", bg: "bg-emerald-50" },
      { label: "Opened", value: `${c.openedCount || 0} (${c.sentCount ? Math.round((c.openedCount / c.sentCount) * 100) : 0}%)`, icon: <Eye className="h-4 w-4" />, color: "text-amber-600", bg: "bg-amber-50" },
      { label: "Clicked", value: `${c.clickedCount || 0} (${c.sentCount ? Math.round((c.clickedCount / c.sentCount) * 100) : 0}%)`, icon: <MousePointerClick className="h-4 w-4" />, color: "text-purple-600", bg: "bg-purple-50" },
      { label: "Bounced", value: c.bouncedCount || 0, icon: <RefreshCw className="h-4 w-4" />, color: "text-orange-600", bg: "bg-orange-50" },
      { label: "Failed", value: c.failedCount || 0, icon: <XCircle className="h-4 w-4" />, color: "text-red-600", bg: "bg-red-50" },
    ];

    return (
      <div>
        {/* Back button + Header */}
        <button
          onClick={() => setSelectedCampaign(null)}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Campaigns
        </button>

        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-800">{c.name}</h1>
              <StatusBadge status={c.status} />
            </div>
            <p className="text-sm text-slate-500 mt-1">Subject: {c.subject}</p>
          </div>
          <div className="flex items-center gap-2">
            {c.status === "draft" && (
              <button
                onClick={() => handleSendCampaign(c._id)}
                className="flex items-center gap-2 px-4 py-2 text-white font-medium rounded-xl transition-colors"
                style={{ backgroundColor: PRIMARY }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = HOVER; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
              >
                <Send className="h-4 w-4" />
                Send Now
              </button>
            )}
            {c.status === "sending" && (
              <button
                onClick={() => handlePauseCampaign(c._id)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-colors"
              >
                <Pause className="h-4 w-4" />
                Pause
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {detailStats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center"
            >
              <div className={`inline-flex p-2 rounded-lg ${s.bg} mb-2`}>
                <span className={s.color}>{s.icon}</span>
              </div>
              <p className="text-lg font-bold text-slate-800">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Recipients Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">Recipients</h3>
            <p className="text-sm text-slate-500">{recipientTotal} total recipients</p>
          </div>
          {loadingRecipients ? (
            <div className="p-8 text-center">
              <div className="animate-spin h-8 w-8 rounded-full border-4 mx-auto mb-3" style={{ borderColor: "#0F5132", borderTopColor: "transparent" }} />
              <p className="text-sm text-slate-500">Loading recipients...</p>
            </div>
          ) : campaignRecipients.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 mb-4">
                <Users className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No recipients yet</p>
              <p className="text-sm text-slate-400 mt-1">Recipients will appear here once the campaign is sent</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sent</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Opened</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignRecipients.map((r, idx) => (
                      <tr key={r._id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-6 py-3 text-sm font-medium text-slate-800">{r.name}</td>
                        <td className="px-6 py-3 text-sm text-slate-600">{r.email}</td>
                        <td className="px-6 py-3"><StatusBadge status={r.status} /></td>
                        <td className="px-6 py-3 text-sm text-slate-500">{r.sentAt ? new Date(r.sentAt).toLocaleString() : "-"}</td>
                        <td className="px-6 py-3 text-sm text-slate-500">{r.openedAt ? new Date(r.openedAt).toLocaleString() : "-"}</td>
                        <td className="px-6 py-3 text-sm text-slate-500">{r.clickedAt ? new Date(r.clickedAt).toLocaleString() : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {recipientTotal > 20 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Page {recipientPage} of {Math.ceil(recipientTotal / 20)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchCampaignRecipients(c._id, recipientPage - 1)}
                      disabled={recipientPage <= 1}
                      className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchCampaignRecipients(c._id, recipientPage + 1)}
                      disabled={recipientPage >= Math.ceil(recipientTotal / 20)}
                      className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Main Layout ─────────────────────────────────────────────────────────

  return (
    <div>
      {/* Email Connection Banner */}
      {hasEmailConnections === false && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3"
        >
          <div className="p-2 rounded-lg bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">Connect your email to start sending campaigns</p>
            <p className="text-xs text-amber-600 mt-0.5">You need at least one email connection to send broadcast campaigns.</p>
          </div>
          <a
            href="/settings/email-connections"
            className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            Connect Email
            <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Radio className="h-6 w-6" style={{ color: PRIMARY }} />
            <h1 className="text-2xl font-bold text-slate-800">Broadcasts</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Create and send email campaigns to your clients
          </p>
        </div>
        {activeTab === "campaigns" && (
          <button
            onClick={() => { resetCampaignForm(); setShowCreateCampaign(true); }}
            className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = HOVER; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
          >
            <Plus className="h-4 w-4" />
            Create Campaign
          </button>
        )}
        {activeTab === "audiences" && (
          <button
            onClick={() => { resetAudienceForm(); setEditingAudience(null); setShowCreateAudience(true); }}
            className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = HOVER; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
          >
            <Plus className="h-4 w-4" />
            Create Audience
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                {loadingCampaigns ? (
                  <div className="h-8 w-24 bg-slate-100 rounded animate-pulse mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
                )}
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="relative flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap"
              style={{ color: activeTab === tab.key ? PRIMARY : "#64748b" }}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="broadcast-tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: PRIMARY }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* ─── Campaigns Tab ─────────────────────────────────────────── */}
          {activeTab === "campaigns" && (
            <div>
              {/* Filters */}
              <div className="flex items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={campaignSearch}
                    onChange={(e) => setCampaignSearch(e.target.value)}
                    placeholder="Search campaigns..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                </div>
                <select
                  value={campaignFilter}
                  onChange={(e) => setCampaignFilter(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="sending">Sending</option>
                  <option value="sent">Sent</option>
                  <option value="paused">Paused</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Campaign List */}
              {loadingCampaigns ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-slate-50 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : campaigns.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-slate-100 mb-4">
                    <Megaphone className="h-10 w-10 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No campaigns yet</h3>
                  <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                    Create your first email campaign to reach your clients with important updates, newsletters, or announcements.
                  </p>
                  <button
                    onClick={() => { resetCampaignForm(); setShowCreateCampaign(true); }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
                    style={{ backgroundColor: PRIMARY }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = HOVER; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
                  >
                    <Plus className="h-4 w-4" />
                    Create Campaign
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((campaign, idx) => (
                    <motion.div
                      key={campaign._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => openCampaignDetail(campaign)}
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm cursor-pointer transition-all group"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="p-2.5 rounded-xl bg-emerald-50 group-hover:bg-emerald-100 transition-colors">
                          <Mail className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-800 truncate">{campaign.name}</h4>
                            <StatusBadge status={campaign.status} />
                          </div>
                          <p className="text-sm text-slate-500 truncate mt-0.5">
                            {campaign.subject}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm text-slate-500">
                        <div className="hidden md:flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          <span>{campaign.recipientCount || 0}</span>
                        </div>
                        {campaign.status === "sent" && (
                          <>
                            <div className="hidden lg:flex items-center gap-1" title="Opened">
                              <Eye className="h-3.5 w-3.5" />
                              <span>{campaign.openedCount || 0}</span>
                            </div>
                            <div className="hidden lg:flex items-center gap-1" title="Clicked">
                              <MousePointerClick className="h-3.5 w-3.5" />
                              <span>{campaign.clickedCount || 0}</span>
                            </div>
                          </>
                        )}
                        <span className="hidden sm:block text-xs text-slate-400">
                          {new Date(campaign.createdAt).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-1">
                          {campaign.status === "draft" && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSendCampaign(campaign._id); }}
                              className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                              title="Send"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          )}
                          {campaign.status === "sending" && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePauseCampaign(campaign._id); }}
                              className="p-1.5 rounded-lg hover:bg-orange-50 text-slate-400 hover:text-orange-600 transition-colors"
                              title="Pause"
                            >
                              <Pause className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDuplicateCampaign(campaign._id); }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Duplicate"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(campaign._id); }}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Audiences Tab ─────────────────────────────────────────── */}
          {activeTab === "audiences" && (
            <div>
              {loadingAudiences ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-slate-50 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : audiences.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-slate-100 mb-4">
                    <Users className="h-10 w-10 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No audiences yet</h3>
                  <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                    Create audiences to organize your clients into groups for targeted email campaigns.
                  </p>
                  <button
                    onClick={() => { resetAudienceForm(); setShowCreateAudience(true); }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
                    style={{ backgroundColor: PRIMARY }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = HOVER; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
                  >
                    <Plus className="h-4 w-4" />
                    Create Audience
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {audiences.map((audience, idx) => (
                    <motion.div
                      key={audience._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm cursor-pointer transition-all group"
                      onClick={() => openEditAudience(audience)}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="p-2.5 rounded-xl bg-blue-50 group-hover:bg-blue-100 transition-colors">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-800">{audience.name}</h4>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              audience.type === "dynamic" ? "bg-purple-50 text-purple-700" : "bg-slate-100 text-slate-600"
                            }`}>
                              {audience.type === "dynamic" ? "Dynamic" : "Static"}
                            </span>
                          </div>
                          {audience.description && (
                            <p className="text-sm text-slate-500 truncate mt-0.5">{audience.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-slate-500">{audience.estimatedCount} recipients</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAudience(audience._id); }}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Unsubscribes Tab ──────────────────────────────────────── */}
          {activeTab === "unsubscribes" && (
            <div className="py-16 text-center">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 mb-4">
                <UserMinus className="h-10 w-10 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No unsubscribes</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                When recipients unsubscribe from your campaigns, they will appear here. Unsubscribed contacts are automatically excluded from future campaigns.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ─── Create Campaign Modal ───────────────────────────────────────── */}
      <PremiumModal
        open={showCreateCampaign}
        onClose={() => { setShowCreateCampaign(false); resetCampaignForm(); }}
        title="Create Campaign"
        subtitle="Set up and send an email campaign to your clients"
        icon={Megaphone}
        size="xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {[0, 1, 2, 3].map((step) => (
                <button
                  key={step}
                  onClick={() => setCampaignStep(step)}
                  className={`h-2 rounded-full transition-all ${
                    campaignStep === step ? "w-8" : "w-2"
                  }`}
                  style={{
                    backgroundColor: campaignStep === step ? "#0F5132" : "#e2e8f0",
                  }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {campaignStep > 0 && (
                <button
                  onClick={() => setCampaignStep((s) => s - 1)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Back
                </button>
              )}
              {campaignStep < 3 ? (
                <button
                  onClick={() => setCampaignStep((s) => s + 1)}
                  className="px-5 py-2 text-sm font-medium text-white rounded-xl transition-colors"
                  style={{ backgroundColor: PRIMARY }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = HOVER; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSaveCampaign}
                  disabled={savingCampaign || !campaignForm.name || !campaignForm.subject}
                  className="px-5 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ backgroundColor: PRIMARY }}
                  onMouseEnter={(e) => { if (!savingCampaign) e.currentTarget.style.backgroundColor = HOVER; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
                >
                  {savingCampaign ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : campaignForm.scheduleType === "scheduled" ? (
                    <>
                      <Clock className="h-4 w-4" />
                      Schedule Campaign
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Create Campaign
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Step 0: Details */}
          {campaignStep === 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg bg-emerald-50">
                  <FileText className="h-4 w-4 text-emerald-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700">Campaign Details</h3>
              </div>

              <FormField label="Campaign Name" required>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Monthly Newsletter - April 2026"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                />
              </FormField>

              <FormField label="Email Connection" required>
                {emailConnections.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Connect your email in{" "}
                      <a href="/settings/email-connections" className="underline font-medium">Settings</a>
                      {" "}to send campaigns
                    </p>
                  </div>
                ) : (
                  <SearchableSelect
                    options={connectionOptions}
                    value={campaignForm.emailConnectionId}
                    onChange={(v) => setCampaignForm((f) => ({ ...f, emailConnectionId: v || "" }))}
                    placeholder="Select email connection..."
                    icon={Mail}
                  />
                )}
              </FormField>

              <FormField label="Audience">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!campaignForm.useManualSelection}
                      onChange={() => setCampaignForm((f) => ({ ...f, useManualSelection: false }))}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm text-slate-700">Select an audience</span>
                  </label>
                  {!campaignForm.useManualSelection && (
                    <SearchableSelect
                      options={audienceOptions}
                      value={campaignForm.audienceId}
                      onChange={(v) => setCampaignForm((f) => ({ ...f, audienceId: v || "" }))}
                      placeholder="Choose audience..."
                      icon={Users}
                    />
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={campaignForm.useManualSelection}
                      onChange={() => setCampaignForm((f) => ({ ...f, useManualSelection: true }))}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm text-slate-700">Select clients manually</span>
                  </label>
                  {campaignForm.useManualSelection && (
                    <div className="space-y-2">
                      <SearchableSelect
                        options={clients.filter((c) => !campaignForm.manualClientIds.includes(c.value))}
                        value={null}
                        onChange={(v) => {
                          if (v) setCampaignForm((f) => ({ ...f, manualClientIds: [...f.manualClientIds, v] }));
                        }}
                        placeholder="Add clients..."
                        icon={Users}
                      />
                      {campaignForm.manualClientIds.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {campaignForm.manualClientIds.map((id) => {
                            const client = clients.find((c) => c.value === id);
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                              >
                                {client?.label || id}
                                <button
                                  onClick={() => setCampaignForm((f) => ({
                                    ...f,
                                    manualClientIds: f.manualClientIds.filter((cid) => cid !== id),
                                  }))}
                                  className="hover:text-red-500 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-xs text-slate-400">
                        {campaignForm.manualClientIds.length} client{campaignForm.manualClientIds.length !== 1 ? "s" : ""} selected
                      </p>
                    </div>
                  )}
                </div>
              </FormField>
            </motion.div>
          )}

          {/* Step 1: Content */}
          {campaignStep === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg bg-blue-50">
                  <Mail className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700">Email Content</h3>
              </div>

              <FormField label="Subject Line" required>
                <input
                  type="text"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="e.g., Important Update from Our Firm"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                />
              </FormField>

              <FormField label="Merge Variables" hint="Click to insert at cursor position">
                <div className="flex flex-wrap gap-2">
                  <MergeChip variable="{{clientName}}" onClick={() => insertMergeVariable("{{clientName}}")} />
                  <MergeChip variable="{{firmName}}" onClick={() => insertMergeVariable("{{firmName}}")} />
                  <MergeChip variable="{{email}}" onClick={() => insertMergeVariable("{{email}}")} />
                </div>
              </FormField>

              <FormField label="Email Body" required>
                <textarea
                  value={campaignForm.body}
                  onChange={(e) => setCampaignForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Write your email content here... HTML is supported."
                  rows={12}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors font-mono resize-y"
                />
              </FormField>

              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-2 text-sm font-medium transition-colors"
                style={{ color: PRIMARY }}
              >
                <Eye className="h-4 w-4" />
                Preview Email
              </button>
            </motion.div>
          )}

          {/* Step 2: Settings */}
          {campaignStep === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg bg-amber-50">
                  <Zap className="h-4 w-4 text-amber-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700">Send Settings</h3>
              </div>

              <FormField label="When to Send">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={campaignForm.scheduleType === "now"}
                      onChange={() => setCampaignForm((f) => ({ ...f, scheduleType: "now" }))}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm text-slate-700">Send immediately after creation</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={campaignForm.scheduleType === "scheduled"}
                      onChange={() => setCampaignForm((f) => ({ ...f, scheduleType: "scheduled" }))}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm text-slate-700">Schedule for later</span>
                  </label>
                  {campaignForm.scheduleType === "scheduled" && (
                    <input
                      type="datetime-local"
                      value={campaignForm.scheduledAt}
                      onChange={(e) => setCampaignForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                    />
                  )}
                </div>
              </FormField>

              <FormField label="Batch Size" hint="Number of emails sent per batch">
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={campaignForm.batchSize}
                    onChange={(e) => setCampaignForm((f) => ({ ...f, batchSize: parseInt(e.target.value) || 25 }))}
                    min={1}
                    max={500}
                    className="w-32 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                  <div className="flex gap-1.5">
                    {[25, 50, 100].map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => setCampaignForm((f) => ({ ...f, batchSize: size }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          campaignForm.batchSize === size
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </FormField>

              <FormField label="Batch Delay" hint="Seconds to wait between batches">
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={campaignForm.batchDelaySecs}
                    onChange={(e) => setCampaignForm((f) => ({ ...f, batchDelaySecs: parseInt(e.target.value) || 1 }))}
                    min={1}
                    max={60}
                    className="w-32 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                  <div className="flex gap-1.5">
                    {[1, 2, 5].map((delay) => (
                      <button
                        key={delay}
                        type="button"
                        onClick={() => setCampaignForm((f) => ({ ...f, batchDelaySecs: delay }))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          campaignForm.batchDelaySecs === delay
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {delay}s
                      </button>
                    ))}
                  </div>
                </div>
              </FormField>
            </motion.div>
          )}

          {/* Step 3: Review */}
          {campaignStep === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg bg-purple-50">
                  <CheckCircle className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700">Review Campaign</h3>
              </div>

              <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                <ReviewRow label="Campaign Name" value={campaignForm.name || "Untitled"} />
                <ReviewRow label="Subject" value={campaignForm.subject || "No subject"} />
                <ReviewRow
                  label="Send From"
                  value={
                    emailConnections.find((c) => c._id === campaignForm.emailConnectionId)?.email || "Not selected"
                  }
                />
                <ReviewRow
                  label="Audience"
                  value={
                    campaignForm.useManualSelection
                      ? `${campaignForm.manualClientIds.length} manually selected client${campaignForm.manualClientIds.length !== 1 ? "s" : ""}`
                      : audiences.find((a) => a._id === campaignForm.audienceId)?.name || "Not selected"
                  }
                />
                <ReviewRow
                  label="Schedule"
                  value={
                    campaignForm.scheduleType === "scheduled" && campaignForm.scheduledAt
                      ? `Scheduled for ${new Date(campaignForm.scheduledAt).toLocaleString()}`
                      : "Send immediately"
                  }
                />
                <ReviewRow label="Batch Size" value={`${campaignForm.batchSize} emails per batch`} />
                <ReviewRow label="Batch Delay" value={`${campaignForm.batchDelaySecs} second${campaignForm.batchDelaySecs !== 1 ? "s" : ""} between batches`} />
              </div>

              {/* Large audience warning */}
              {(() => {
                const selectedAudience = audiences.find((a) => a._id === campaignForm.audienceId);
                const recipientCount = campaignForm.useManualSelection
                  ? campaignForm.manualClientIds.length
                  : selectedAudience?.estimatedCount || 0;
                if (recipientCount > 100) {
                  return (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-700">
                        This will send to <strong>{recipientCount}</strong> recipients. Please confirm this is correct.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </motion.div>
          )}
        </div>
      </PremiumModal>

      {/* ─── Email Preview Modal ─────────────────────────────────────────── */}
      <PremiumModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        title="Email Preview"
        subtitle="Preview how your email will appear to recipients"
        icon={Eye}
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Subject</p>
            <p className="text-sm font-medium text-slate-800">{campaignForm.subject || "No subject"}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6 min-h-[200px]">
            {campaignForm.body ? (
              <div
                className="prose prose-sm max-w-none text-slate-700"
                dangerouslySetInnerHTML={{
                  __html: campaignForm.body
                    .replace(/\{\{clientName\}\}/g, '<span class="bg-emerald-100 text-emerald-800 px-1 rounded font-medium">John Doe</span>')
                    .replace(/\{\{firmName\}\}/g, '<span class="bg-emerald-100 text-emerald-800 px-1 rounded font-medium">Your Firm Name</span>')
                    .replace(/\{\{email\}\}/g, '<span class="bg-emerald-100 text-emerald-800 px-1 rounded font-medium">john@example.com</span>')
                    .replace(/\n/g, "<br />"),
                }}
              />
            ) : (
              <p className="text-slate-400 text-sm italic">No content to preview. Go back and add your email body.</p>
            )}
          </div>
        </div>
      </PremiumModal>

      {/* ─── Create/Edit Audience Modal ──────────────────────────────────── */}
      <PremiumModal
        open={showCreateAudience}
        onClose={() => { setShowCreateAudience(false); setEditingAudience(null); resetAudienceForm(); }}
        title={editingAudience ? "Edit Audience" : "Create Audience"}
        subtitle="Define a group of recipients for your campaigns"
        icon={Users}
        size="lg"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <button
              onClick={() => { setShowCreateAudience(false); setEditingAudience(null); resetAudienceForm(); }}
              className="px-4 py-2 text-sm font-medium text-slate-600 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAudience}
              disabled={savingAudience || !audienceForm.name}
              className="px-5 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ backgroundColor: PRIMARY }}
              onMouseEnter={(e) => { if (!savingAudience) e.currentTarget.style.backgroundColor = HOVER; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}
            >
              {savingAudience ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  {editingAudience ? "Update Audience" : "Create Audience"}
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <FormField label="Audience Name" required>
            <input
              type="text"
              value={audienceForm.name}
              onChange={(e) => setAudienceForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., All Real Estate Clients"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </FormField>

          <FormField label="Description">
            <input
              type="text"
              value={audienceForm.description}
              onChange={(e) => setAudienceForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Brief description of this audience..."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </FormField>

          <FormField label="Type">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAudienceForm((f) => ({ ...f, type: "static" }))}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border transition-colors ${
                  audienceForm.type === "static"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Users className="h-4 w-4" />
                  Static
                </div>
                <p className="text-xs mt-1 opacity-70">Manually select clients</p>
              </button>
              <button
                type="button"
                onClick={() => setAudienceForm((f) => ({ ...f, type: "dynamic" }))}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border transition-colors ${
                  audienceForm.type === "dynamic"
                    ? "border-purple-300 bg-purple-50 text-purple-700"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Zap className="h-4 w-4" />
                  Dynamic
                </div>
                <p className="text-xs mt-1 opacity-70">Auto-filter by criteria</p>
              </button>
            </div>
          </FormField>

          {/* Static: Client picker */}
          {audienceForm.type === "static" && (
            <FormField label="Select Clients">
              <div className="space-y-2">
                <SearchableSelect
                  options={clients.filter((c) => !audienceForm.clientIds.includes(c.value))}
                  value={null}
                  onChange={(v) => {
                    if (v) setAudienceForm((f) => ({ ...f, clientIds: [...f.clientIds, v] }));
                  }}
                  placeholder="Add clients..."
                  icon={Users}
                />
                {audienceForm.clientIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {audienceForm.clientIds.map((id) => {
                      const client = clients.find((c) => c.value === id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                        >
                          {client?.label || id}
                          <button
                            onClick={() => setAudienceForm((f) => ({
                              ...f,
                              clientIds: f.clientIds.filter((cid) => cid !== id),
                            }))}
                            className="hover:text-red-500 transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  {audienceForm.clientIds.length} client{audienceForm.clientIds.length !== 1 ? "s" : ""} selected
                </p>
              </div>
            </FormField>
          )}

          {/* Dynamic: Filter form */}
          {audienceForm.type === "dynamic" && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter Criteria</p>

              <FormField label="Practice Areas">
                <input
                  type="text"
                  value={audienceForm.filters.practiceAreas.join(", ")}
                  onChange={(e) =>
                    setAudienceForm((f) => ({
                      ...f,
                      filters: {
                        ...f.filters,
                        practiceAreas: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      },
                    }))
                  }
                  placeholder="e.g., real_estate, immigration (comma-separated)"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors bg-white"
                />
              </FormField>

              <FormField label="Tags">
                <input
                  type="text"
                  value={audienceForm.filters.tags.join(", ")}
                  onChange={(e) =>
                    setAudienceForm((f) => ({
                      ...f,
                      filters: {
                        ...f.filters,
                        tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      },
                    }))
                  }
                  placeholder="e.g., VIP, newsletter (comma-separated)"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors bg-white"
                />
              </FormField>

              <FormField label="Matter Status">
                <select
                  value={audienceForm.filters.matterStatus}
                  onChange={(e) =>
                    setAudienceForm((f) => ({
                      ...f,
                      filters: { ...f.filters, matterStatus: e.target.value },
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors bg-white"
                >
                  <option value="">Any status</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="pending">Pending</option>
                </select>
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="From Date">
                  <input
                    type="date"
                    value={audienceForm.filters.dateRange.from}
                    onChange={(e) =>
                      setAudienceForm((f) => ({
                        ...f,
                        filters: {
                          ...f.filters,
                          dateRange: { ...f.filters.dateRange, from: e.target.value },
                        },
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors bg-white"
                  />
                </FormField>
                <FormField label="To Date">
                  <input
                    type="date"
                    value={audienceForm.filters.dateRange.to}
                    onChange={(e) =>
                      setAudienceForm((f) => ({
                        ...f,
                        filters: {
                          ...f.filters,
                          dateRange: { ...f.filters.dateRange, to: e.target.value },
                        },
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors bg-white"
                  />
                </FormField>
              </div>
            </div>
          )}
        </div>
      </PremiumModal>
    </div>
  );
};

// ─── Review Row Helper ─────────────────────────────────────────────────────

const ReviewRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800">{value}</span>
  </div>
);

export default BroadcastsPage;
